import { Accordion, Button, RadioGroup } from "../components/rumonai";
import { LightMotion } from "../components/LightMotion";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import { settingsFeed } from "../lib/settingsFeed";
import { PlatformError, usePlatformCommand, usePlatformRead } from "../platform/shared";
import { hub, modelSupplyPath, modelSourcePath } from "./api";
import { useAutoRefresh } from "./autoRefresh";
import {
  ServicePrice, modelSourceView, platformCatalog,
  type KindSource, type ModelSourceView, type PlatformCatalog,
} from "./modelSupply";
import "./ModelSourceSettings.css";
import "../connections/settings-list.css";

const SOURCES = { platform: "涟漪提供", own: "自带服务" } as const;
type Source = keyof typeof SOURCES;

export function AccountModelUsage() {
  const reading = usePlatformRead<PlatformCatalog>(modelSupplyPath);
  // The balance moves while the Agents work, and the Hub has no stream for it.
  useAutoRefresh(reading.reload);
  const data = platformCatalog(reading.data);
  const error =
    reading.error ||
    (reading.data !== undefined && !data
      ? new ApiError("模型额度未读完整", "请重新读取后查看可用额度。", null, 502)
      : null);
  const usage = data?.usage;
  return <section className="settings-group account-usage" aria-label="模型与额度">
    <h2 className="settings-group-title">模型与额度</h2>
    <div className="settings-list account-usage-card">
      {usage ? (
        <>
          <dl className="usage-stats">
            <div className="usage-stat usage-stat-main"><dt>可用额度</dt><dd className="num">{formatMoney({ amount: usage.remaining, currency: usage.currency })}</dd></div>
            {usage.spend ? <div className="usage-stat"><dt>已用</dt><dd className="num">{usage.spend.figure}</dd></div> : null}
            {usage.reserved ? <div className="usage-stat"><dt>{usage.unknown_calls ? "预留" : "结算中"}</dt>
              <dd className="num">{formatMoney({ amount: usage.reserved, currency: usage.currency })}</dd></div> : null}
          </dl>
          {/* Said right under the figures: they count the platform's own services, so a zero here is not "nothing was asked". */}
          {usage.spend ? <p className="usage-caption">涟漪提供的服务：{usage.spend.note}。</p> : null}
          <p className="usage-caption">额度用于 Agent 调用模型；所有 Agent 共用，自带服务单独计费。</p>
          {/* An account nobody gave an allowance has used nothing up: only an administrator can give one. */}
          {usage.allowance === 0 && usage.calls === 0 ? <p className="settings-notice">还没有平台额度，请联系平台管理员发放。</p> : null}
          {usage.unknown_calls > 0 ? <p className="usage-caption">{usage.unknown_calls} 笔费用待平台确认</p> : null}
        </>
      ) : !error ? (
        <p className="usage-caption" role="status"><LightMotion state="working" compact />正在读取…</p>
      ) : null}
      {error ? <PlatformError error={error} retry={reading.reload} /> : null}
    </div>
  </section>;
}

/**
 * Where each kind of service this Agent uses comes from: the platform, or the
 * owner's own keys. One kind changes per request, and a row shows the source
 * the Hub answered with, never the one clicked before it answered.
 */
export function ModelSourceSettings({ agentId, online, namespace, namespaces }: {
  agentId: string; online: boolean; namespace: string; settingTitle?: string; namespaces?: string[];
}) {
  const reading = usePlatformRead<ModelSourceView>(modelSourcePath(agentId));
  const [pricing, setPricing] = useState<Record<string, string[]>>({});
  const command = usePlatformCommand();
  const { reload } = reading;
  const readAgain = reload;
  // The first render reads already; the Agent connecting or leaving changes what can be asked.
  const shownOnline = useRef(online);
  useEffect(() => {
    if (shownOnline.current === online) return;
    shownOnline.current = online;
    readAgain();
  }, [online, readAgain]);
  const view = modelSourceView(reading.data);
  // Supply can change while this Agent stays online or offline; a saved response can go stale too.
  useAutoRefresh(readAgain, { every: 10_000 });
  const error =
    reading.error ||
    (reading.data !== undefined && !view ? new ApiError("服务来源未读完整", "请重新读取后再切换。", null, 502) : null);
  const editable = Boolean(view?.online) && online && !command.pending;
  const choose = (row: KindSource, source: Source, reapply = false) => {
    if (!editable || row.source === source && !reapply) return;
    void command.run(async () => {
      let response: ModelSourceView;
      try {
        response = await hub.setModelSource(agentId, row.kind, source);
      } catch (failure) {
        readAgain(); // Whether it switched is read back, not assumed.
        throw failure;
      } finally {
        // The settings this kind feeds may be on this page too (a connection's form, the default chat model):
        // they read again now, not when the Agent's stream gets round to saying they were written.
        for (const written of row.namespaces) settingsFeed.publish({ namespace: written });
      }
      if (!modelSourceView(response)) {
        readAgain();
        throw new ApiError("服务来源未读完整", "已重新读取当前来源。", null, 502);
      }
      if (!reading.accept(response)) return;
      command.setNotice(reapply ? "已重新应用平台服务" : "已保存");
    });
  };
  const rows = view?.kinds.filter(row => (namespaces ?? [namespace]).some(consumer => row.namespaces.includes(consumer)));
  if (rows && !rows.length && !error) return null;
  return <section className="model-source-settings pa-open-group" aria-label="服务来源">
    <header className="model-source-heading"><h3>服务来源</h3></header>
    {view && !view.online ? <p className="model-source-waiting">连接后可切换</p> : null}
    {command.error ? <PlatformError error={command.error} /> : null}
    {error ? <PlatformError error={error} retry={readAgain} /> : null}
    {view ? (
      <div className="model-source-kinds">
        {rows?.map(row => {
          // Source preference survives a paused offer. Availability does not select personal credentials.
          const offered = row.services.length > 0;
          const note = `model-source-note-${row.kind}`;
          return <fieldset key={row.kind} className="model-source-kind" aria-label={row.title}>
            <legend>{row.title}</legend>
            {row.namespaces.length > 1 ? <p className="micro model-source-shared">同一 IP 的{row.title}功能共用。</p> : null}
            <RadioGroup className="model-source-options" label={`${row.title}服务来源`} orientation="horizontal"
              name={`model-source-${agentId}-${row.kind}`} value={row.source} disabled={!editable}
              onValueChange={source => choose(row, source as Source)}
              items={(Object.keys(SOURCES) as Source[]).map(source => ({ value: source, label: SOURCES[source], disabled: source === "platform" && !offered, descriptionId: source === "platform" && (!offered || row.platform_connection === "needs_apply" || row.platform_connection === "unknown") ? note : undefined }))} />
            {!offered ? <small className="model-source-state is-unavailable" id={note}>平台暂未提供此服务。</small> : null}
            {row.source === "own" && offered ? <small className="model-source-state">在下方填写连接信息。</small> : null}
            {row.source === "platform" && offered ? row.platform_connection === "needs_apply" ? (
              <small className="model-source-state is-pending" id={note} role="status">已选择涟漪提供，连接尚未就绪。请重新应用平台服务后再使用。</small>
            ) : row.platform_connection === "unknown" ? (
              <small className="model-source-state" id={note}>平台连接尚未确认，连接后可重新读取。</small>
            ) : row.platform_connection === "unavailable" ? <small className="model-source-state is-unavailable">平台连接暂不可用，可稍后重新读取。</small> : null : null}
            {row.source === "platform" && offered && row.services.length === 1 ? <p className="model-source-price-preview">费用：<ServicePrice rate={row.services[0]} /></p> : null}
            {row.source === "platform" && offered && row.platform_connection === "needs_apply" ? <Button className="model-source-reapply" variant="ghost" disabled={!editable}
              onClick={() => choose(row, "platform", true)}>重新应用平台服务</Button> : null}
            {row.source === "platform" && offered ? <Accordion value={pricing[row.kind] ?? []}
              onValueChange={value => setPricing(current => ({ ...current, [row.kind]: value }))}
              items={[{ value: "prices", title: "服务详情与价格", content: <>
                <p>这个 IP 内使用{row.title}的功能共用此来源选择。</p>
                {row.platform_connection === "ready" ? <p><strong>平台连接已配置</strong>，实际调用结果在使用时确认。</p> : null}
                <ul>{row.services.map((service, index) => <li key={index}>{service.title} · <ServicePrice rate={service} /></li>)}</ul>
              </> }]} /> : null}
          </fieldset>;
        })}
      </div>
    ) : !error ? <p role="status"><LightMotion state="working" compact />正在读取服务来源…</p> : null}
    {command.notice ? <span role="status" className="model-source-receipt">{command.notice}</span> : null}
  </section>;
}
